import { Field, Form as FormikForm, Formik } from 'formik'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { post } from 'axios'
import {
    Button, Form, Icon,
} from 'semantic-ui-react'
import { URL } from '../../../store/path'
import { FormikDateComponent, FormikSelectComponent } from '../../../utilities/formUtils'
import { heightSet } from '../../../utilities/heightSet'
import { TableListDiv } from '../../archivedata/components/tableListDiv'
import { setAlertModalState } from '../../common/data/actions'
import { fetchUploadedFiles, resetUploadedFiles } from '../data/actions'
import { extractAxiosError, extractErrorMessage } from '../data/errorMsg'
import { getSearchColumns, uploaderListSearch, uploaderTypeOptions } from '../data/model'
import { getIsFetching, getUploadedFiles } from '../data/selectors'
import UploaderModal from './uploaderModal'


const UploaderList = (props) => {
    const dispatch = useDispatch()
    const uploadedFiles = useSelector(getUploadedFiles)
    const isFetching = useSelector(getIsFetching)
    const [heights, setHeight] = useState('')
    const [modalOpen, setModalOpen] = useState(false)
    const [lastSearch, setLastSearch] = useState(null)
    const [downloadingId, setDownloadingId] = useState(null)

    useEffect(() => {
        dispatch(resetUploadedFiles())
    }, [])

    const handleSearch = (values) => {
        const missing = []
        if (!values.from_date) missing.push('From Date')
        if (!values.to_date) missing.push('To Date')
        if (!values.type) missing.push('Type')
        if (missing.length > 0) {
            dispatch(setAlertModalState({
                isOpen: true,
                type: 'error',
                size: 'mini',
                title: 'Required Fields',
                msg: `${missing.join(', ')} ${missing.length > 1 ? 'are' : 'is'} required.`,
            }))
            return
        }
        if (values.from_date > values.to_date) {
            dispatch(setAlertModalState({
                isOpen: true,
                type: 'error',
                size: 'mini',
                title: 'Invalid Date Range',
                msg: 'From Date should not be greater than To Date.',
            }))
            return
        }
        const payload = {
            from_date: values.from_date,
            to_date: values.to_date,
            type: values.type,
            account: localStorage.getItem('acc') || '',
        }
        setLastSearch(payload)
        dispatch(fetchUploadedFiles(payload))
    }

    const refetchAfterUpload = () => {
        if (lastSearch) {
            dispatch(fetchUploadedFiles(lastSearch))
        }
    }

    const callHeightFunc = () => {
        heightSet(setHeight)
    }

    useEffect(() => {
        callHeightFunc();
        window.addEventListener("resize", callHeightFunc, false);
        return () => {
            window.removeEventListener("resize", callHeightFunc);
        };
    }, []);

    const handleDownload = async (id, fileName, downloadType = 'Error') => {
        if (!id) return
        setDownloadingId(id + ':' + downloadType)
        try {
            const response = await post(
                URL + '/upload/master/dwnl4mS3/' + id,
                { download_type: downloadType },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('user')}`,
                        'X-RLB-DMS-ACC': localStorage.getItem('acc'),
                        'Content-Type': 'application/json',
                    },
                    responseType: 'blob',
                }
            )
            const contentType = response.headers['content-type'] || ''
            if (contentType.includes('application/json')) {
                const text = await response.data.text()
                let json = {}
                try { json = JSON.parse(text) } catch (e) { json = { data: text } }
                dispatch(setAlertModalState({
                    isOpen: true,
                    type: 'error',
                    size: 'mini',
                    title: 'Download Error',
                    msg: extractErrorMessage(json, 'Failed to download error file.'),
                }))
                return
            }
            const isExcel = /\.xlsx?$/i.test(fileName || '')
            const blobType = isExcel
                ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                : (contentType || 'application/octet-stream')
            const blob = new Blob([response.data], { type: blobType })
            const downloadUrl = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.style.display = 'none'
            a.href = downloadUrl
            a.download = fileName || `download_${id}.xlsx`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(downloadUrl)
        } catch (error) {
            const msg = await extractAxiosError(error, 'Failed to download error file.')
            dispatch(setAlertModalState({
                isOpen: true,
                type: 'error',
                size: 'mini',
                title: 'Download Error',
                msg,
            }))
        } finally {
            setDownloadingId(null)
        }
    }

    const handleDownloadOriginal = (id, fileName) => handleDownload(id, fileName, 'original')
    const handleDownloadError = (id, fileName) => handleDownload(id, fileName, 'Error')

    const columns = useMemo(
        () => getSearchColumns({
            onDownloadOriginal: handleDownloadOriginal,
            onDownloadError: handleDownloadError,
            downloadingId,
        }),
        [downloadingId]
    )

    const totalRecords = Array.isArray(uploadedFiles) ? uploadedFiles.length : 0

    return (
        <>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 4px 12px',
                gap: '12px',
                flexWrap: 'wrap',
            }}>
                <div>
                    <div style={{ fontSize: '17px', fontWeight: 600, color: '#1f2d3d', lineHeight: 1.2 }}>
                        Uploader
                    </div>
                    <div style={{ fontSize: '12px', color: '#8a96a8', marginTop: '2px' }}>
                        View uploaded files and upload new master data via Excel.
                    </div>
                </div>
                <Formik
                    id='uploaderSearch'
                    initialValues={uploaderListSearch()}
                    onSubmit={(values) => handleSearch(values)}
                    render={({ handleSubmit }) => (
                        <Form as={FormikForm} size='small' onSubmit={handleSubmit} style={{ marginBottom: 0 }}>
                            <Form.Group style={{ marginBottom: 0, alignItems: 'flex-end' }}>
                                <Field
                                    name='from_date'
                                    component={FormikDateComponent}
                                    placeholder='From Date'
                                    isTxn={false}
                                    isLabel={true}
                                />
                                <Field
                                    name='to_date'
                                    component={FormikDateComponent}
                                    placeholder='To Date'
                                    isTxn={false}
                                    isLabel={true}
                                />
                                <Field
                                    name='type'
                                    component={FormikSelectComponent}
                                    isSelection={true}
                                    placeholder='Type'
                                    isTxn={false}
                                    isLabel={true}
                                    userProps={{ options: uploaderTypeOptions }}
                                />
                                <Button
                                    type='submit'
                                    size='small'
                                    color='blue'
                                    icon
                                    labelPosition='left'
                                    loading={isFetching}
                                    disabled={isFetching}
                                >
                                    <Icon name='search' />
                                    Search
                                </Button>
                                <Button
                                    type='button'
                                    size='small'
                                    color='green'
                                    icon
                                    labelPosition='left'
                                    onClick={() => setModalOpen(true)}
                                >
                                    <Icon name='cloud upload' />
                                    Upload New File
                                </Button>
                            </Form.Group>
                        </Form>
                    )}
                />
            </div>

            <div style={{ paddingTop: '10px', position: 'relative' }}>
                <TableListDiv
                    columns={columns}
                    data={uploadedFiles || []}
                    setHeight={parseFloat(heights) - 180}
                    getIndex={0}
                    filterBoxNone={''}
                    rightBox={''}
                    centerBox={''}
                    callHeightFunc={callHeightFunc}
                    paddingShort={2}
                    getWidth={'100%'}
                    actionClass={''}
                />
                {totalRecords === 0 && !isFetching && (
                    <div style={{
                        position: 'absolute',
                        top: '80px',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#9aa6b1',
                        pointerEvents: 'none',
                    }}>
                        <Icon name='inbox' size='big' style={{ color: '#cfd8e3' }} />
                        <div style={{ marginTop: '6px', fontSize: '13px' }}>
                            No uploaded files found. Apply filters and click Search.
                        </div>
                    </div>
                )}
            </div>

            <UploaderModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onUploaded={refetchAfterUpload}
            />
        </>
    )
}

export default UploaderList
